import { clone, cloneDeep, get, isEqual, merge, set } from 'lodash';
import moment from 'moment';
import i18n from '@/plugins/i18n';

import { default as _Vue } from 'vue';
import { AbstractService } from '@/plugins/podolib/lib/AbstractService';
import { Store } from 'vuex';

import { TEntity, TFetch } from '@/plugins/podolib/services/entities/Entity';
import { TPatient } from '@/plugins/podolib/services/entities/EntityPatients';
import { TStation } from '@/plugins/podolib/services/entities/EntityStations';
import { TSoles } from '@/plugins/podolib/services/entities/EntitySoles';
import {
  CommandName,
  CommandSettings,
  CommandStatus,
  CommandStep,
  TCommand,
} from '@/plugins/podolib/services/entities/EntityCommands';

// Step types
import {
  AnalysisRunningMode,
  StepType,
  AnalysisType,
  AnalysisWalkingMode,
} from '@/components/analysis/steps/type/AnalysisStepType';
import {
  ConditionsPainLocations,
  ConditionsPainSide,
  ConditionsPainZone_1,
  ConditionsPainZone_2_C1,
  ConditionsShoesTypeCushioning,
  ConditionsShoesTypeRigidity,
  ConditionsShoesTypeWear,
  ConditionsWalkingAidDevices,
  StepConditions,
} from '@/components/analysis/steps/conditions/AnalysisStepConditions';
import { StepStation } from '@/components/analysis/steps/station/AnalysisStepStation';
import { StepSolesPair } from '@/components/analysis/steps/solesPair/AnalysisStepSolesPair';
import { StepAnalysis } from '@/components/analysis/steps/analysis/AnalysisStepAnalysis';
import { Severity } from '@sentry/types';
import { gte } from 'semver';

export type Options = {};

export enum StepsKeys {
  Type = 'type',
  Conditions = 'conditions',
  Station = 'station',
  SolesPair = 'soles-pair',
  Analysis = 'analysis',
}

export type Step<S> = S & {
  __system: {
    touched: boolean;
    dirty: boolean;
  };
};

export type Manifest = {
  [StepsKeys.Type]: Step<StepType>;
  [StepsKeys.Conditions]: Step<StepConditions>;
  [StepsKeys.Station]: Step<StepStation>;
  [StepsKeys.SolesPair]: Step<StepSolesPair>;
  [StepsKeys.Analysis]: Step<StepAnalysis>;
};

// Store state type
export type State = {
  patientId: TEntity<TPatient>['_id'] | null;
  manifest: Manifest;
  steps: StepsKeys[];
  currentStepKey: StepsKeys;
  entities: {
    patient: TEntity<TPatient> | null;
    stations: TFetch<TEntity<TStation>> | null;
    solesPairs: TFetch<TEntity<TSoles>> | null;
    command: TEntity<TCommand> | null;
  };
};

export enum Mutation {
  // SET
  SET__PATIENT_ID = 'SET__PATIENT_ID',
  SET__MANIFEST = 'SET__MANIFEST',
  SET__STEPS = 'SET__STEPS',
  SET__CURRENT_STEP_KEY = 'SET__CURRENT_STEP_KEY',
  SET__ENTITY__PATIENT = 'SET__ENTITY__PATIENT',
  SET__ENTITY__STATIONS = 'SET__ENTITY__STATIONS',
  SET__ENTITY__SOLES_PAIRS = 'SET__ENTITY__SOLES_PAIRS',
  SET__ENTITY__COMMAND = 'SET__ENTITY__COMMAND',
  // CREATE
  CREATE__ENTITY__SOLES_PAIR = 'CREATE__ENTITY__SOLES_PAIR',
  // PATCH
  PATCH__ENTITY__STATION = 'PATCH__ENTITY__STATION',
  PATCH__ENTITY__SOLES_PAIR = 'PATCH__ENTITY__SOLES_PAIR',
  // DELETE
  DELETE__ENTITY__SOLES_PAIR = 'DELETE__ENTITY__SOLES_PAIR',
  // CLEAR
  CLEAR__PATIENT_ID = 'CLEAR__PATIENT_ID',
  CLEAR__MANIFEST = 'CLEAR__MANIFEST',
  CLEAR__CURRENT_STEP_KEY = 'CLEAR__CURRENT_STEP_KEY',
  CLEAR__ENTITY__PATIENT = 'CLEAR__ENTITY__PATIENT',
  CLEAR__ENTITY__STATIONS = 'CLEAR__ENTITY__STATIONS',
  CLEAR__ENTITY__SOLES_PAIRS = 'CLEAR__ENTITY__SOLES_PAIRS',
  CLEAR__ENTITY__COMMAND = 'CLEAR__ENTITY__COMMAND',
}

export type StepsWithLabel = {
  key: StepsKeys;
  label: string;
};

type AnalysisFinishedResultError =
  | {
      error: string;
      error_code: 'NOT_ENOUGH_STEPS';
      error_status: 'INVALID';
    }
  | {
      error: string;
      error_code: 'INVALID_DISTANCE';
      error_status: 'INVALID';
    }
  | {
      error: string;
      error_code: 'INVALID_TIME';
      error_status: 'INVALID';
    }
  | {
      error: string;
      error_code: 'EXCEPTION';
      error_status: 'EXCEPTION';
    }
  | {
      error: undefined;
      error_code: undefined;
      error_status: undefined;
    };

export type AnalysisFinishedResult = {
  _id?: TEntity<TPatient>['_id'];
  commandId: TEntity<TCommand>['_id'];
  data: TCommand['data'] | any;
  infos?: TPatient['infos'];
} & AnalysisFinishedResultError;

type AnalysisFinishedResultFetched = {
  _id: string;
  latestCommand: TEntity<TCommand>['_id'];
  acquisitionId: string;
} & AnalysisFinishedResultError;

/**
 * Commands
 *
 * start acquisition		sent
 * start acquisition		received
 * start acquisition		pending     scanning
 * start acquisition		pending     connecting
 * start acquisition		pending     started
 * start acquisition		completed
 *
 * acquisition data     sent
 * acquisition data     received
 * acquisition data     pending     scanning
 * acquisition data     pending     connecting
 * acquisition data     pending     getting_data
 * acquisition data     completed
 */

/**
 * Analysis service
 *
 * @author Damien CORLER <d.corler@zhortech.com>
 * @since 2.1.5
 */
export default class AnalysisService extends AbstractService<Options, State> {
  // Interval for timer in step 'analysis'
  timerInterval: any = null;
  // Store memory
  storeMemory: State;
  // Cleaning in progress
  cleaningInProgress = false;

  /**
   * @param options
   */
  constructor(options?: Options) {
    super(options, 'analysis');

    // Trick to avoid conflict of 'this' with event emitter
    this.onStationPatched = this.onStationPatched.bind(this);
    this.onSolesPairAddedOrDeleted = this.onSolesPairAddedOrDeleted.bind(this);
    this.onSolesPairPatched = this.onSolesPairPatched.bind(this);
    this.onCommandCreatedOrPatched = this.onCommandCreatedOrPatched.bind(this);
    this.onCommandDeleted = this.onCommandDeleted.bind(this);
    this.onAcquisitionFinished = this.onAcquisitionFinished.bind(this);

    this.storeMemory = cloneDeep(this.defaultState);
  }

  /**
   * Initialize plugin
   */
  public async init(Vue: typeof _Vue, store: Store<any>) {
    await super.init(Vue, store);

    const __this = this;

    this.$register(this.defaultState, {
      mutations: {
        // SET ---------------------------------------------------------------------------------------------------------
        [Mutation.SET__PATIENT_ID](state, patientId: TEntity<TPatient>['_id'] | null) {
          Vue.set(state, 'patientId', patientId);
        },
        [Mutation.SET__MANIFEST](state, manifest: Manifest) {
          Vue.set(state, 'manifest', manifest);
        },
        [Mutation.SET__STEPS](state, steps: StepsKeys[]) {
          Vue.set(state, 'steps', steps);
        },
        [Mutation.SET__CURRENT_STEP_KEY](state, currentStepKey: StepsKeys) {
          Vue.set(state, 'currentStepKey', currentStepKey);
        },
        [Mutation.SET__ENTITY__PATIENT](state, patient: TEntity<TPatient> | null) {
          Vue.set(state.entities, 'patient', patient);
        },
        [Mutation.SET__ENTITY__STATIONS](state, stations: TFetch<TEntity<TStation>> | null) {
          Vue.set(state.entities, 'stations', stations);
        },
        [Mutation.SET__ENTITY__SOLES_PAIRS](state, solesPairs: TFetch<TEntity<TSoles>> | null) {
          Vue.set(state.entities, 'solesPairs', solesPairs);
        },
        [Mutation.SET__ENTITY__COMMAND](state, command: TEntity<TCommand> | null) {
          Vue.set(state.entities, 'command', command);
        },

        // CREATE ------------------------------------------------------------------------------------------------------
        [Mutation.CREATE__ENTITY__SOLES_PAIR](state, solesPair: TEntity<TSoles>) {
          const _targetIndex = state.entities.solesPairs!.data.findIndex((x) => x._id === solesPair._id);
          if (_targetIndex >= 0) {
            state.entities.solesPairs!.data.push(solesPair);
            state.entities.solesPairs!.total++;
          }
        },

        // PATCH -------------------------------------------------------------------------------------------------------
        [Mutation.PATCH__ENTITY__STATION](state, station: TEntity<TStation>) {
          const _targetIndex = state.entities.stations!.data.findIndex((x) => x._id === station._id);
          if (_targetIndex >= 0) {
            Vue.set(state.entities.stations!.data, _targetIndex, station);
          }
        },
        [Mutation.PATCH__ENTITY__SOLES_PAIR](state, solesPair: TEntity<TSoles>) {
          const _targetIndex = state.entities.solesPairs!.data.findIndex((x) => x._id === solesPair._id);
          if (_targetIndex >= 0) {
            Vue.set(
              state.entities.solesPairs!.data,
              _targetIndex,
              merge(state.entities.solesPairs!.data[_targetIndex], solesPair),
            );
          }
        },

        // DELETE ------------------------------------------------------------------------------------------------------
        [Mutation.DELETE__ENTITY__SOLES_PAIR](state, solesPair: TEntity<TSoles>) {
          const _targetIndex = state.entities.solesPairs!.data.findIndex((x) => x._id === solesPair._id);
          if (_targetIndex >= 0) {
            state.entities.solesPairs!.data = state.entities.solesPairs!.data.splice(_targetIndex, 1);
            state.entities.solesPairs!.total--;
          }
        },

        // CLEAR -------------------------------------------------------------------------------------------------------
        [Mutation.CLEAR__PATIENT_ID](state) {
          Vue.set(state, 'patientId', __this.defaultState.patientId);
        },
        [Mutation.CLEAR__MANIFEST](state) {
          Vue.set(state, 'manifest', __this.defaultState.manifest);
        },
        [Mutation.CLEAR__CURRENT_STEP_KEY](state) {
          Vue.set(state, 'currentStepKey', __this.defaultState.currentStepKey);
        },
        [Mutation.CLEAR__ENTITY__PATIENT](state) {
          Vue.set(state.entities, 'patient', __this.defaultState.entities.patient);
        },
        [Mutation.CLEAR__ENTITY__STATIONS](state) {
          Vue.set(state.entities, 'stations', __this.defaultState.entities.stations);
        },
        [Mutation.CLEAR__ENTITY__SOLES_PAIRS](state) {
          Vue.set(state.entities, 'solesPairs', __this.defaultState.entities.solesPairs);
        },
        [Mutation.CLEAR__ENTITY__COMMAND](state) {
          Vue.set(state.entities, 'command', __this.defaultState.entities.command);
        },
      },
    });

    // Watch store mutations
    this.store.watch(
      (state) => get(state, this._rootPath),
      (newState: State) => {
        // Check if current state and memory are different
        if (!isEqual(newState, this.storeMemory)) {
          // Automatically select station
          const selectStation = () => {
            if (!newState.patientId) return;

            const onlineStations = this.entityStations!.data.filter((station) => station.online);
            const oldStateStepStation = this.getManifestEntry(StepsKeys.Station, this.storeMemory);

            if (
              !oldStateStepStation.stationId ||
              !this.entityStations!.data.find((station) => station._id === oldStateStepStation.stationId)?.online
            ) {
              if (onlineStations.length === 1 && !oldStateStepStation.stationId) {
                if (!oldStateStepStation.__system.dirty) {
                  this.$commit(Mutation.SET__STEPS, [
                    StepsKeys.Type,
                    StepsKeys.Conditions,
                    StepsKeys.SolesPair,
                    StepsKeys.Analysis,
                  ]);

                  this.logger.log('Select station');
                  // Select station
                  this.setPropertyInManifestEntry(StepsKeys.Station, 'stationId', onlineStations[0]._id, true);
                }
              } else {
                this.logger.log('Station must be selected');
                this.$commit(Mutation.SET__STEPS, this.defaultState.steps);
                if (!oldStateStepStation.__system.dirty) {
                  // Reset station
                  this.setPropertyInManifestEntry(StepsKeys.Station, 'stationId', null, true);
                }
              }
            }
          };

          // Set default values
          const setDefaultValues = () => {
            if (this.analysisOrComputeInProgress) return;

            const oldStateStepType = this.getManifestEntry(StepsKeys.Type, this.storeMemory);
            const newStateStepType = this.getManifestEntry(StepsKeys.Type);

            if (isEqual(oldStateStepType, newStateStepType)) return;

            this.logger.log('Set default values...');

            switch (newStateStepType.type) {
              case AnalysisType.Walking:
                this.setPropertyInManifestEntry(StepsKeys.Analysis, 'speed', null);
                switch (newStateStepType.mode) {
                  case AnalysisWalkingMode.Standard:
                    this.setPropertyInManifestEntry(StepsKeys.Analysis, 'duration', null);
                    break;
                  case AnalysisWalkingMode.Research:
                    this.setPropertyInManifestEntry(StepsKeys.Analysis, 'duration', 120000);
                    break;
                }
                break;
              case AnalysisType.Running:
                this.setPropertyInManifestEntry(StepsKeys.Analysis, 'duration', 120000);
                switch (newStateStepType.mode) {
                  case AnalysisRunningMode.Outdoor:
                    this.setPropertyInManifestEntry(StepsKeys.Analysis, 'speed', null);
                    break;
                  case AnalysisRunningMode.Treadmill:
                    this.setPropertyInManifestEntry(StepsKeys.Analysis, 'speed', 15);
                    break;
                }
                break;
              default:
                this.setPropertyInManifestEntry(StepsKeys.Analysis, 'duration', null);
                this.setPropertyInManifestEntry(StepsKeys.Analysis, 'speed', null);
                break;
            }
          };

          // Timer
          const toggleTimer = () => {
            const oldStateStepAnalysis = this.getManifestEntry(StepsKeys.Analysis, this.storeMemory);
            const newStateStepAnalysis = this.getManifestEntry(StepsKeys.Analysis);

            if (newStateStepAnalysis.flags.analysis.isStarted === oldStateStepAnalysis.flags.analysis.isStarted) return;

            if (
              newStateStepAnalysis.flags.analysis.isStarted &&
              !newStateStepAnalysis.flags.analysis.waitGettingData &&
              !newStateStepAnalysis.flags.analysis.waitCancelled
            ) {
              this.startTimer();
            } else {
              this.stopTimer();
            }
          };

          if (!this.cleaningInProgress) {
            selectStation();
            setDefaultValues();
            toggleTimer();
          }

          // Update memory
          this.storeMemory = cloneDeep(newState);
        }
      },
      { immediate: true, deep: true },
    );
  }

  /**
   * Get default state of this store module
   */
  public get defaultState(): State {
    // Clone it to avoid side effect with reactive system
    const __system = { dirty: false, touched: false };

    return {
      // ...super.defaultState,
      patientId: null,
      steps: [StepsKeys.Type, StepsKeys.Conditions, StepsKeys.Station, StepsKeys.SolesPair, StepsKeys.Analysis],
      currentStepKey: StepsKeys.Type,
      manifest: {
        [StepsKeys.Type]: { mode: null, type: null, __system: clone(__system) },
        [StepsKeys.Conditions]: {
          pains: [],
          shoes_type: null,
          walking_aids: [],
          pathologies: [],
          __system: clone(__system),
        },
        [StepsKeys.Station]: { stationId: null, __system: clone(__system) },
        [StepsKeys.SolesPair]: { solesPairId: null, __system: clone(__system) },
        [StepsKeys.Analysis]: {
          delay: 0,
          duration: null,
          speed: null,
          observation: '',
          flags: {
            analysis: {
              isInternal: false,
              isPending: false,
              waitStarted: false,
              isStarted: false,
              waitGettingData: false,
              isGettingData: false,
              waitCancelled: false,
              isCancelled: false,
              isSuccess: false,
              isFailure: false,
            },
            compute: {
              isComputing: false,
              isSuccess: false,
              isFailure: false,
            },
          },
          timer: {
            startDate: null,
            endDate: null,
            current: null,
            completed: false,
          },
          warn: null,
          error: null,
          acquisitionId: null,
          __system: clone(__system),
        },
      },
      entities: {
        patient: null,
        stations: null,
        solesPairs: null,
        command: null,
      },
    };
  }

  // -------------------------------------------------------------------------------------------------------------------

  /**
   * Start service
   * - create listeners
   * - fetch stations, soles and latest command
   */
  public async start() {
    this.logger.log('⏳ Warm up...');

    // Create listeners
    this.createListeners();
    // Fetch stations, pairs of soles and latest command
    await Promise.all([this.fetchStations(), this.fetchSolesPairs(), this.fetchLatestCommand()]);

    if (this.entityStations!.total > 0) {
      if (this.entityCommand) {
        await this.configurePatient(this.entityCommand.patientId);
      }
    }

    this.logger.info('🎉 Started');
  }

  /**
   * Configure service with patient target
   */
  public async configurePatient(patientId: TEntity<TPatient>['_id']) {
    this.logger.info('⏳ Configure patient...');

    this.clean(
      this.getManifestEntry(StepsKeys.Analysis).flags.analysis.waitCancelled ||
        (this.getManifestEntry(StepsKeys.Analysis).flags.analysis.isSuccess &&
          this.getManifestEntry(StepsKeys.Analysis).flags.compute.isSuccess),
    );

    // Set patient identifier
    this.$commit(Mutation.SET__PATIENT_ID, patientId);

    // Fetch patient
    await this.fetchPatient();

    // this.configureManifest();

    this.computeCommand(true);

    // Check if an analysis is in calculation
    if (
      this.entityCommand?.command === CommandName.AcquisitionData &&
      this.entityCommand?.status === CommandStatus.Completed
    ) {
      this.logger.log('Analysis completed in memory, check calcul unit...');

      const calculUnit = await this.fetchCalculUnit();

      if (calculUnit) {
        this.onAcquisitionFinished(calculUnit);
      }
    }

    this.computeStep();

    this.logger.success('✅ Patient configured');
  }

  /**
   * Configure manifest with current command
   */
  public configureManifest() {
    if (!this.entityCommand) return;

    this.logger.info('Configure manifest...');

    this.setManifestEntry(StepsKeys.Type, {
      type: this.entityCommand.data.analysisType,
      mode: this.entityCommand.data.analysisMode,
    });
    this.setManifestEntry(StepsKeys.Station, { stationId: this.entityCommand.stationId });
    this.setManifestEntry(StepsKeys.SolesPair, { solesPairId: this.entityCommand.solesId });

    this.setPropertyInManifestEntry(StepsKeys.Analysis, 'delay', this.entityCommand?.data.params?.aqDelay);
    this.setPropertyInManifestEntry(StepsKeys.Analysis, 'duration', this.entityCommand?.data.params?.aqDuration);
    this.setPropertyInManifestEntry(StepsKeys.Analysis, 'speed', this.entityCommand?.data.treadmill_speed);
    this.setPropertyInManifestEntry(StepsKeys.Analysis, 'observation', this.entityCommand?.data.observation);
  }

  /**
   * Clean service
   */
  public clean(force = false) {
    this.logger.log('Clean');

    this.cleaningInProgress = true;

    // If manifest contain data...
    if (this.manifestHasData) {
      // Clear manifest
      this.$commit(Mutation.CLEAR__MANIFEST);
    }

    // Reset patient identifier
    this.$commit(Mutation.CLEAR__PATIENT_ID);
    // Reset patient entity
    this.$commit(Mutation.CLEAR__ENTITY__PATIENT);
    if (force) {
      // Reset command entity
      this.$commit(Mutation.CLEAR__ENTITY__COMMAND);
    }
    // Reset current step
    this.currentStepKey = StepsKeys.Type;

    this.Vue.prototype.$nextTick(() => {
      this.cleaningInProgress = false;
    });
  }

  /**
   * Create listeners
   * - patch station
   * - patch pair of soles
   * - create command
   * - patch command
   * - delete command
   */
  private createListeners() {
    // Stations
    this.Vue.prototype.$pdc.bus.on('entities:stations:patched', this.onStationPatched);

    // Pairs of soles
    this.Vue.prototype.$pdc.bus.on('entities:soles:assigned', this.onSolesPairAddedOrDeleted);
    this.Vue.prototype.$pdc.bus.on('entities:soles:unassigned', this.onSolesPairAddedOrDeleted);
    this.Vue.prototype.$pdc.bus.on('entities:soles:patched', this.onSolesPairPatched);

    // Commands
    this.Vue.prototype.$pdc.bus.on('entities:commands:created', this.onCommandCreatedOrPatched);
    this.Vue.prototype.$pdc.bus.on('entities:commands:patched', this.onCommandCreatedOrPatched);
    this.Vue.prototype.$pdc.bus.on('entities:commands:removed', this.onCommandDeleted);

    this.Vue.prototype.$pdc.repository.socket.session.on('patients acquisitionFinished', this.onAcquisitionFinished);
  }

  private onStationPatched(station: TEntity<TStation>) {
    return this.$commit(Mutation.PATCH__ENTITY__STATION, station);
  }

  // TODO : optimize this
  private async onSolesPairAddedOrDeleted() {
    return await this.fetchSolesPairs();
  }

  private onSolesPairPatched(solesPair: TEntity<TSoles>) {
    return this.$commit(Mutation.PATCH__ENTITY__SOLES_PAIR, solesPair);
  }

  private onCommandCreatedOrPatched(command: TEntity<TCommand>) {
    // Get current command
    const _command = this.entityCommand;

    if (
      !this.Vue.prototype.$pdc.helper
        .enumToArray(CommandName)
        .map((value: any) => value.value)
        .includes(command.command)
    ) {
      return;
    }

    // Compare 'updatedAt' property : if more recent overwrite current command
    if (!_command || moment(_command.updatedAt).diff(command.updatedAt) < 0) {
      if (!_command && !this.patientId) {
        this.logger.info('Remote action received, transfert...');
        this.configurePatient(command.patientId)
          .then(() => {
            this.logger.success('Remote action transferred');
          })
          .catch((reason) => {
            this.logger.error(reason);
          });
      }

      this.$commit(Mutation.SET__ENTITY__COMMAND, command);

      return this.computeCommand();
    }
  }

  private onCommandDeleted(command: TEntity<TCommand>) {
    // Get current command
    const _command = this.entityCommand;

    // Delete command only if it's the same '_id' property
    if (_command?._id === command._id) {
      this.$commit(Mutation.SET__ENTITY__COMMAND, null);

      return this.computeCommand();
    }
  }

  private onAcquisitionFinished(result: AnalysisFinishedResult | AnalysisFinishedResultFetched) {
    if (
      (result as AnalysisFinishedResult).commandId === this.entityCommand?._id ||
      (result as AnalysisFinishedResultFetched).latestCommand === this.entityCommand?._id
    ) {
      if (result.error) {
        switch (result.error_code) {
          case 'NOT_ENOUGH_STEPS':
          case 'INVALID_DISTANCE':
          case 'INVALID_TIME':
            // Set 'error'
            this.setPropertyInManifestEntry(
              StepsKeys.Analysis,
              'error',
              new Error(i18n.t(`analysis.steps.analysis.compute.errors.${result.error_code}`).toString()),
            );
            // Report to sentry
            this.Vue.prototype.$sentry.captureException(result.error_code);
            break;
          default:
            // Set 'error'
            this.setPropertyInManifestEntry(
              StepsKeys.Analysis,
              'error',
              new Error(i18n.t('analysis.steps.analysis.errors.unknown').toString()),
            );
            // Report to sentry
            this.Vue.prototype.$sentry.captureException(result);
            break;
        }

        // Up 'isFailure' flag
        this.upFlagsAndDownOthers(StepsKeys.Analysis, 'compute', ['isFailure']);
      } else {
        // Reset 'error'
        this.setPropertyInManifestEntry(StepsKeys.Analysis, 'error', null);

        // Set 'acquisitionId'
        this.setPropertyInManifestEntry(
          StepsKeys.Analysis,
          'acquisitionId',
          (result as AnalysisFinishedResult).data._id,
        );

        // Up 'isSuccess' flag
        this.upFlagsAndDownOthers(StepsKeys.Analysis, 'compute', ['isSuccess']);
      }
    }
  }

  // -------------------------------------------------------------------------------------------------------------------

  /**
   * Start an analysis
   */
  async startAnalysis() {
    const stepAnalysis = this.getManifestEntry(StepsKeys.Analysis);

    if (stepAnalysis.flags.analysis.isPending || stepAnalysis.flags.compute.isComputing) return;

    try {
      this.logger.info('Start analysis...');

      // Reset 'error'
      this.setPropertyInManifestEntry(StepsKeys.Analysis, 'error', null);
      // Reset 'warn'
      this.setPropertyInManifestEntry(StepsKeys.Analysis, 'warn', null);

      // Up "isInternal" flag
      this.setPropertyInManifestEntry(StepsKeys.Analysis, 'flags.analysis.isInternal', true);
      // Up "isPending" flag
      this.setPropertyInManifestEntry(StepsKeys.Analysis, 'flags.analysis.isPending', true);
      // Up "waitStarted" flag
      this.setPropertyInManifestEntry(StepsKeys.Analysis, 'flags.analysis.waitStarted', true);

      // Down "isCancelled" flag
      this.setPropertyInManifestEntry(StepsKeys.Analysis, 'flags.analysis.isCancelled', false);
      // Down "isSuccess" flag
      this.setPropertyInManifestEntry(StepsKeys.Analysis, 'flags.analysis.isSuccess', false);

      // Emit command
      await this.Vue.prototype.$pdc.repository!.socket.emitCommand(
        'start acquisition',
        this.getAnalysisSettings('start'),
      );
    } catch (e) {
      // Set error
      this.setPropertyInManifestEntry(StepsKeys.Analysis, 'error', e);
      // Down "isInternal" flag
      this.setPropertyInManifestEntry(StepsKeys.Analysis, 'flags.analysis.isInternal', false);
      // Down "pending" flag
      this.setPropertyInManifestEntry(StepsKeys.Analysis, 'flags.analysis.isPending', false);
      // Down "waitStarted" flag
      this.setPropertyInManifestEntry(StepsKeys.Analysis, 'flags.analysis.waitStarted', false);

      throw e;
    }
  }

  /**
   * Stop an analysis
   */
  async stopAnalysis() {
    const stepAnalysis = this.getManifestEntry(StepsKeys.Analysis);

    if (stepAnalysis.flags.analysis.isPending || stepAnalysis.flags.compute.isComputing) return;

    try {
      this.logger.info('Stop analysis...');

      // Reset 'error'
      this.setPropertyInManifestEntry(StepsKeys.Analysis, 'error', null);
      // Reset 'warn'
      this.setPropertyInManifestEntry(StepsKeys.Analysis, 'warn', null);

      // Up "isPending" flag
      this.setPropertyInManifestEntry(StepsKeys.Analysis, 'flags.analysis.isPending', true);
      // Up "waitGettingData" flag
      this.setPropertyInManifestEntry(StepsKeys.Analysis, 'flags.analysis.waitGettingData', true);

      // Emit command
      await this.Vue.prototype.$pdc.repository!.socket.emitCommand(
        'acquisition data',
        this.getAnalysisSettings('stop'),
      );
    } catch (e) {
      // Set error
      this.setPropertyInManifestEntry(StepsKeys.Analysis, 'error', e);
      // Down "pending" flag
      this.setPropertyInManifestEntry(StepsKeys.Analysis, 'flags.analysis.isPending', false);
      // Down "waitGettingData" flag
      this.setPropertyInManifestEntry(StepsKeys.Analysis, 'flags.analysis.waitGettingData', false);
    }
  }

  /**
   * Cancel an analysis
   */
  async cancelAnalysis() {
    this.logger.info('Cancel analysis...');

    try {
      // Reset 'error'
      this.setPropertyInManifestEntry(StepsKeys.Analysis, 'error', null);
      // Reset 'warn'
      this.setPropertyInManifestEntry(StepsKeys.Analysis, 'warn', null);

      // Up "isPending" flag
      this.setPropertyInManifestEntry(StepsKeys.Analysis, 'flags.analysis.isPending', true);
      // Up "waitCancelled" flag
      this.setPropertyInManifestEntry(StepsKeys.Analysis, 'flags.analysis.waitCancelled', true);

      if (this.entityCommand) {
        // Emit command
        await this.Vue.prototype.$pdc.repository!.socket.deleteAllCommands(this.Vue.prototype.$pdc.doctor.current._id);
      } else {
        this.upFlagsAndDownOthers(StepsKeys.Analysis, 'analysis', ['isCancelled']);
        this.upFlagsAndDownOthers(StepsKeys.Analysis, 'compute', []);
      }
    } catch (e) {
      // Set error
      this.setPropertyInManifestEntry(StepsKeys.Analysis, 'error', e);
      // Down "pending" flag
      this.setPropertyInManifestEntry(StepsKeys.Analysis, 'flags.analysis.isPending', false);
      // Down "waitCancelled" flag
      this.setPropertyInManifestEntry(StepsKeys.Analysis, 'flags.analysis.waitCancelled', false);
    }
  }

  private getAnalysisSettings(type?: 'start' | 'stop') {
    // Retrieve steps' manifest entries
    const stepType = this.getManifestEntry(StepsKeys.Type);
    const { __system, ...stepConditions } = this.getManifestEntry(StepsKeys.Conditions);
    const stepStation = this.getManifestEntry(StepsKeys.Station);
    const stepSolesPair = this.getManifestEntry(StepsKeys.SolesPair);
    const stepAnalysis = this.getManifestEntry(StepsKeys.Analysis);

    // Create payload
    let settings: CommandSettings = {
      patientId: this.patientId!,
      stationId: stepStation.stationId!,
      solesId: stepSolesPair.solesPairId!,
      data: {
        analysisType: stepType.type!,
        analysisMode: stepType.mode!,
        mode: 'standard',
        conditions: stepConditions,
        observation: stepAnalysis.observation,
      },
    };

    switch (true) {
      // Running
      case stepType.type === AnalysisType.Running:
        settings.data.mode = 'segmented';
        settings.data.params = {
          aqDelay: stepAnalysis.delay,
          aqDuration: stepAnalysis.duration,
        };

        switch (stepType.mode) {
          case AnalysisRunningMode.Treadmill:
            settings.data.treadmill_speed = stepAnalysis.speed;
            break;
        }
        break;
      // Research
      case stepType.mode === AnalysisWalkingMode.Research:
        settings.data.mode = type === 'start' ? 'standard' : 'segmented';
        settings.data.params = {
          aqDelay: stepAnalysis.delay,
          aqDuration: stepAnalysis.duration,
          memsConfig: {
            gyroscope: {
              config_flags: {
                CTRL7_G_G_HM_MODE: 0,
              },
            },
            accelerometer: {
              full_scale: 8,
              config_flags: {
                CTRL6_C_XL_HM_MODE: 0,
              },
            },
          },
        };
        break;
      // Walk with soles ~2.7.3
      case stepType.mode === AnalysisWalkingMode.Standard && type && type === 'stop':
        const selectedSoles = this.Vue.prototype.$pdc.analysis!.entitySolesPairs.data.find(
          (solesPair: any) => solesPair._id === stepSolesPair.solesPairId,
        );
        if (selectedSoles) {
          const solesAdaptedForSegmentedMode = gte(
            `${selectedSoles.left.firmware.maj}.${selectedSoles.left.firmware.min}.${selectedSoles.left.firmware.build}`,
            '2.7.3',
          );
          if (solesAdaptedForSegmentedMode) settings.data.mode = 'segmented';
        }
        break;
    }

    return settings;
  }

  // -------------------------------------------------------------------------------------------------------------------

  /**
   * Compute current command
   */
  private computeCommand(canBeEmpty = false) {
    const stepAnalysis = this.getManifestEntry(StepsKeys.Analysis);

    // If NO command exist in store
    if (!this.entityCommand) {
      if (canBeEmpty) return;

      // If wait cancellation...
      if (stepAnalysis.flags.analysis.waitCancelled) {
        // Emit
        this.Vue.prototype.$pdc.bus!.emitDebug('analysis:step-analysis:cancelled');
      }
      // If cancellation come from another tab / process / device
      else if (!stepAnalysis.flags.compute.isSuccess) {
        // Set 'warn'
        this.setPropertyInManifestEntry(
          StepsKeys.Analysis,
          'warn',
          new Error(i18n.t('analysis.steps.analysis.cancelled-another-tab-or-device').toString()),
        );
      }

      // Up "isCancelled" flag and down others
      this.upFlagsAndDownOthers(StepsKeys.Analysis, 'analysis', ['isCancelled']);
      this.upFlagsAndDownOthers(StepsKeys.Analysis, 'compute', []);

      // Reset 'acquisitionId'
      this.setPropertyInManifestEntry(StepsKeys.Analysis, 'acquisitionId', null);
      // Reset 'error'
      this.setPropertyInManifestEntry(StepsKeys.Analysis, 'error', null);
    }
    // If command exist in store
    else {
      this.configureManifest();

      // else if (!this.data.flags.isSuccess && !this.data.flags.waitCancelled) {
      switch (this.entityCommand.status) {
        // Command is sent
        case CommandStatus.Sent:
          switch (this.entityCommand.command) {
            case CommandName.StartAcquisition:
              this.upFlagsAndDownOthers(StepsKeys.Analysis, 'analysis', ['isPending', 'waitStarted'], ['isInternal']);

              break;
            case CommandName.AcquisitionData:
              this.upFlagsAndDownOthers(
                StepsKeys.Analysis,
                'analysis',
                ['isPending', 'isStarted', 'waitGettingData'],
                ['isInternal'],
              );
              break;
          }

          this.upFlagsAndDownOthers(StepsKeys.Analysis, 'compute', []);

          this.cleanErrorsAndWarns(StepsKeys.Analysis);
          this.currentStepKey = StepsKeys.Analysis;
          break;

        // Command is received
        case CommandStatus.Received:
          switch (this.entityCommand.command) {
            case CommandName.StartAcquisition:
              this.upFlagsAndDownOthers(StepsKeys.Analysis, 'analysis', ['isPending', 'waitStarted'], ['isInternal']);

              break;
            case CommandName.AcquisitionData:
              this.upFlagsAndDownOthers(
                StepsKeys.Analysis,
                'analysis',
                ['isPending', 'isStarted', 'waitGettingData'],
                ['isInternal'],
              );
              break;
          }

          this.upFlagsAndDownOthers(StepsKeys.Analysis, 'compute', []);

          this.cleanErrorsAndWarns(StepsKeys.Analysis);
          this.currentStepKey = StepsKeys.Analysis;
          break;

        // Command is pending
        case CommandStatus.Pending:
          switch (this.entityCommand.command) {
            case CommandName.StartAcquisition:
              this.upFlagsAndDownOthers(StepsKeys.Analysis, 'analysis', ['isPending', 'waitStarted'], ['isInternal']);
              break;
            case CommandName.AcquisitionData:
              if (this.entityCommand.data.step === CommandStep.GettingData) {
                this.upFlagsAndDownOthers(StepsKeys.Analysis, 'analysis', ['isGettingData'], ['isInternal']);
              } else {
                this.upFlagsAndDownOthers(
                  StepsKeys.Analysis,
                  'analysis',
                  ['isPending', 'isStarted', 'waitGettingData'],
                  ['isInternal'],
                );
              }

              break;
          }
          this.upFlagsAndDownOthers(StepsKeys.Analysis, 'compute', []);

          this.cleanErrorsAndWarns(StepsKeys.Analysis);
          this.currentStepKey = StepsKeys.Analysis;
          break;

        // Command is completed
        case CommandStatus.Completed:
          switch (this.entityCommand.command) {
            case CommandName.StartAcquisition:
              this.upFlagsAndDownOthers(StepsKeys.Analysis, 'analysis', ['isStarted'], ['isInternal']);
              this.upFlagsAndDownOthers(StepsKeys.Analysis, 'compute', []);
              break;
            case CommandName.AcquisitionData:
              this.entityCommand.data.progress = 100;

              this.logger.info('Analysis ready for calculation');

              this.upFlagsAndDownOthers(StepsKeys.Analysis, 'analysis', ['isSuccess'], ['isInternal']);
              this.upFlagsAndDownOthers(StepsKeys.Analysis, 'compute', ['isComputing']);
              break;
          }

          this.cleanErrorsAndWarns(StepsKeys.Analysis);
          this.currentStepKey = StepsKeys.Analysis;
          break;

        // Command failed
        case CommandStatus.Error:
          this.cleanErrorsAndWarns(StepsKeys.Analysis);

          if (
            this.entityCommand.data?.error?.code &&
            [1001, 1002, 2001, 2002, 2003, 2004, 2005, 2006, 2007, 2008].indexOf(
              this.entityCommand.data.error.code as number,
            ) > -1
          ) {
            // Set 'warn'
            this.setPropertyInManifestEntry(
              StepsKeys.Analysis,
              'warn',
              new Error(i18n.t(`acquisition.errors.station.${this.entityCommand.data.error!.code}`).toString()),
            );
            // Sentry
            this.Vue.prototype.$sentry.captureMessage(this.entityCommand.data.error!.code.toString(), Severity.Warning);
          } else {
            // Set 'error'
            this.setPropertyInManifestEntry(
              StepsKeys.Analysis,
              'error',
              new Error(i18n.t('analysis.steps.analysis.errors.unknown').toString()),
            );
            // Sentry
            this.Vue.prototype.$sentry.captureException({ message: 'Unknown error', command: this.entityCommand });
          }

          switch (this.entityCommand.command) {
            case CommandName.StartAcquisition:
              this.upFlagsAndDownOthers(StepsKeys.Analysis, 'analysis', [], ['isInternal']);
              break;
            case CommandName.AcquisitionData:
              this.upFlagsAndDownOthers(StepsKeys.Analysis, 'analysis', ['isStarted'], ['isInternal']);
              break;
          }

          this.upFlagsAndDownOthers(StepsKeys.Analysis, 'compute', []);

          this.currentStepKey = StepsKeys.Analysis;
          break;

        // Unknown
        default:
          this.upFlagsAndDownOthers(StepsKeys.Analysis, 'analysis', []);
          this.upFlagsAndDownOthers(StepsKeys.Analysis, 'compute', []);
          break;
      }
    }
  }

  /**
   * Flag helper
   */
  private upFlagsAndDownOthers<K extends 'analysis' | 'compute'>(
    stepKey: StepsKeys.Analysis,
    key: K,
    flags: (keyof StepAnalysis['flags'][K])[],
    skip: (keyof StepAnalysis['flags'][K])[] = [],
  ) {
    const step = this.getManifestEntry(stepKey);

    const _f: StepAnalysis['flags'] = Object.keys(step.flags[key]).reduce((acc, _k) => {
      if (!skip.includes(_k as keyof StepAnalysis['flags'][K])) {
        const value = flags.includes(_k as keyof StepAnalysis['flags'][K]);
        return { ...acc, [_k]: value };
      } else {
        return { ...acc, [_k]: step.flags[key][_k as keyof StepAnalysis['flags'][K]] };
      }
    }, {} as StepAnalysis['flags']);

    this.setPropertyInManifestEntry(stepKey, `flags.${key}`, _f);
  }

  /**
   * Errors and warns cleaner helper
   */
  private cleanErrorsAndWarns(stepKey: StepsKeys.Analysis) {
    const step = this.getManifestEntry(stepKey);

    if (Object.prototype.hasOwnProperty.call(step, 'error')) this.setPropertyInManifestEntry(stepKey, 'error', null);
    if (Object.prototype.hasOwnProperty.call(step, 'warn')) this.setPropertyInManifestEntry(stepKey, 'warn', null);
  }

  // -------------------------------------------------------------------------------------------------------------------

  /**
   * Start acquisition timer
   */
  private startTimer() {
    if (!this.entityCommand) return;

    const stepAnalysis = this.getManifestEntry(StepsKeys.Analysis);

    this.logger.log('Start timer');

    if (this.timerInterval) clearInterval(this.timerInterval);

    const startDate = moment(stepAnalysis.flags.analysis.isInternal ? moment() : this.entityCommand.updatedAt).add(
      stepAnalysis.delay,
      'milliseconds',
    );
    const endDate = moment(startDate).add(stepAnalysis.duration ?? 60000, 'millisecond');

    this.timerInterval = setInterval(() => {
      this.updateTimer();
    }, 1000);

    this.updateTimer({ startDate, endDate });
  }

  /**
   * Stop acquisition timer
   */
  private stopTimer() {
    const timer = this.getManifestEntry(StepsKeys.Analysis).timer;

    if (!timer.current) return;

    this.logger.log('Stop timer');

    if (this.timerInterval) clearInterval(this.timerInterval);

    this.setPropertyInManifestEntry(StepsKeys.Analysis, 'timer', {
      startDate: null,
      endDate: null,
      current: null,
      completed: false,
    });
  }

  /**
   * Update timer
   */
  private updateTimer(t?: { startDate: any; endDate: any }) {
    if (!this.entityCommand) return;

    const stepAnalysis = this.getManifestEntry(StepsKeys.Analysis);

    if (!(t?.startDate ?? stepAnalysis.timer.startDate)) {
      this.stopTimer();
      return;
    }

    let _current = moment();

    if (!stepAnalysis.flags.analysis.isInternal) {
      _current = _current.add(this.Vue.prototype.$pdc.commons.timeDrift, 'milliseconds');
    }

    let current = _current.diff(t?.startDate ?? stepAnalysis.timer.startDate);

    current = Math.floor(current / 1000) * 1000;

    const completed =
      cloneDeep(t?.startDate ?? stepAnalysis.timer.startDate)
        .add(current, 'milliseconds')
        .diff(t?.endDate ?? stepAnalysis.timer.endDate) >= 0;

    this.setPropertyInManifestEntry(StepsKeys.Analysis, 'timer', {
      ...stepAnalysis.timer,
      ...t,
      current,
      completed,
    });
  }

  // -------------------------------------------------------------------------------------------------------------------

  private computeStep() {
    const manifest = this.manifest;

    this.logger.log('Select step');

    // Type
    if (!manifest[StepsKeys.Type].type) {
      this.logger.log('Go to "type" step');
      this.currentStepKey = StepsKeys.Type;
      return;
    }

    // Skip conditions

    // Station
    if (!manifest[StepsKeys.Station].stationId) {
      this.logger.log('Go to "station" step');
      this.currentStepKey = StepsKeys.Station;
      return;
    }

    // Pair of soles
    if (!manifest[StepsKeys.SolesPair].solesPairId) {
      this.logger.log('Go to "solesPair" step');
      this.currentStepKey = StepsKeys.SolesPair;
      return;
    }

    // Analysis
    if (
      !manifest[StepsKeys.Analysis].flags.analysis.isSuccess ||
      manifest[StepsKeys.Analysis].flags.compute.isComputing ||
      manifest[StepsKeys.Analysis].flags.compute.isSuccess ||
      manifest[StepsKeys.Analysis].flags.compute.isFailure
    ) {
      this.logger.log('Go to "analysis" step');
      this.currentStepKey = StepsKeys.Analysis;
      return;
    }

    this.logger.log('Go to default step');
    this.currentStepKey = StepsKeys.Type;
  }

  /**
   * Increase step
   */
  public increaseStep() {
    const currentStepIndex = Object.values(this.stepsWithLabel.map((x) => x.key)).indexOf(this.currentStepKey);
    this.currentStepKey = this.steps[currentStepIndex + 1];
  }

  // -------------------------------------------------------------------------------------------------------------------

  /**
   * Retrieve patient's data from the api
   * - fetch patient
   */
  private async fetchPatient(): Promise<TEntity<TPatient>> {
    // Try to retrieve patient's data from the api...
    const patient = await this.Vue.prototype.$pdc.repository!.getRessource({
      identifier: this.patientId,
      ressource: 'patients',
      params: {
        $select: [
          '_id',
          'infos.firstName',
          'infos.lastName',
          'infos.footSize',
          'infos.pathologies',
          'infos.customPathologies',
        ],
        $populate: [
          {
            path: 'infos.pathologies.reference',
          },
        ],
      },
    });

    // Store patient
    this.$commit(Mutation.SET__ENTITY__PATIENT, patient);

    return patient;
  }

  /**
   * Retrieve calcul unit's data from the api
   * - fetch calcul unit
   */
  private async fetchCalculUnit(): Promise<any> {
    if (!this.entityCommand) return null;

    // Try to retrieve calcul unit's data from the api...
    const commands = await this.Vue.prototype.$pdc.repository!.fetchRessource({
      ressource: 'acquisitionstatus',
      params: {
        latestCommand: this.entityCommand!._id,
        $limit: 1,
        $sort: {
          updatedAt: -1,
        },
      },
    });

    return commands.data[0] ?? null;
  }

  /**
   * Retrieve stations' data from the api
   * - fetch stations
   */
  private async fetchStations(): Promise<TFetch<TEntity<TStation>>> {
    // Try to retrieve stations' data from the api...
    const stations = await this.Vue.prototype.$pdc.repository!.fetchRessource({
      ressource: 'stations',
      params: {
        $select: ['_id', 'name', 'online'],
        $limit: 50,
      },
    });

    // Store stations
    this.$commit(Mutation.SET__ENTITY__STATIONS, stations);

    return stations;
  }

  /**
   * Retrieve pairs of soles' data from the api
   * - fetch pairs of soles
   */
  private async fetchSolesPairs(): Promise<TFetch<TEntity<TSoles>>> {
    // Try to retrieve stations' data from the api...
    const solesPairs = await this.Vue.prototype.$pdc.repository!.fetchRessource({
      ressource: 'soles',
      params: {
        $select: ['_id', 'abbr', 'left.footSize', 'left.battery', 'right.battery', 'left.firmware', 'right.firmware'],
        $limit: 50,
      },
    });

    // Store solesPairs
    this.$commit(Mutation.SET__ENTITY__SOLES_PAIRS, solesPairs);

    return solesPairs;
  }

  /**
   * Retrieve latest command's data from the api (related to patient)
   * - fetch latest command
   */
  private async fetchLatestCommand(): Promise<TEntity<TCommand>> {
    // Try to retrieve commands' data from the api...
    const commands = await this.Vue.prototype.$pdc.repository!.fetchRessource({
      ressource: 'commands',
      params: {
        command: { $in: this.Vue.prototype.$pdc.helper.enumToArray(CommandName).map((value: any) => value.value) },
        $sort: {
          updatedAt: -1,
        },
        $limit: 1,
      },
    });

    // Store command
    this.$commit(Mutation.SET__ENTITY__COMMAND, commands.data[0] ?? null);

    return commands.data[0] ?? null;
  }

  /**
   * Use a predefined manifest for development
   */
  public useDebugManifest() {
    const p: any = {
      [StepsKeys.Type]: {
        type: AnalysisType.Running,
        mode: AnalysisRunningMode.Treadmill,
      },
      [StepsKeys.Conditions]: {
        pains: [
          {
            location: ConditionsPainLocations.Thigh,
            side: ConditionsPainSide.Right,
            zone_1: ConditionsPainZone_1.Side,
            zone_2: ConditionsPainZone_2_C1.Posterior,
            level: 8,
          },
        ],
        shoes_type: {
          rigidity: ConditionsShoesTypeRigidity.Moderate,
          cushioning: ConditionsShoesTypeCushioning.Moderate,
          wear: ConditionsShoesTypeWear.PosteroMedian,
        },
        walking_aids: [ConditionsWalkingAidDevices.SimpleCane],
      },
      [StepsKeys.Station]: {
        stationId: '5db6fee31857680028e70532',
      },
      [StepsKeys.SolesPair]: {
        solesPairId: '5d14c74a5d60a768eb9654e9',
      },
      [StepsKeys.Analysis]: {
        delay: 10000,
        duration: 60000,
        speed: 15,
        flags: {
          isPending: false,
          waitStarted: false,
          isStarted: false,
          waitGettingData: false,
          isGettingData: false,
          waitCancelled: false,
          isCancelled: false,
          isSuccess: false,
        },
        timer: { startDate: null, endDate: null, current: null, completed: false },
        warn: null,
        error: null,
        acquisitionId: null,
      },
    };

    Object.entries(p).forEach(([key, value]) => {
      this.setManifestEntry(key as StepsKeys, {
        ...(value as any),
      });
    });

    this.computeStep();
  }

  // -------------------------------------------------------------------------------------------------------------------

  /**
   * Get an entry of manifest
   */
  public getManifestEntry<K extends StepsKeys>(stepKey: K, source?: State): Manifest[K] {
    if (source) {
      return cloneDeep(source['manifest'][stepKey]);
    } else {
      return cloneDeep(this.$state<Manifest>('manifest')[stepKey]);
    }

    // return cloneDeep(this.$getters('getManifestEntry', stepKey));
  }

  /**
   * Add or edit an entry in manifest
   */
  public setManifestEntry<K extends StepsKeys>(stepKey: K, data: Omit<Manifest[K], '__system'>, automated = false) {
    const manifest = this.$state<Manifest>('manifest');

    this.$commit(Mutation.SET__MANIFEST, {
      ...manifest,
      [stepKey]: merge(cloneDeep(data), {
        __system: {
          dirty: true,
          touched: manifest[stepKey].__system.touched || !automated,
        },
      }),
    });
  }

  /**
   * Add or edit a property of an entry in manifest
   */
  public setPropertyInManifestEntry<K extends StepsKeys, I extends Omit<Manifest[K], '__system'>>(
    stepKey: K,
    key: string,
    data: any,
    automated = false,
  ) {
    const manifest = this.$state<Manifest>('manifest');
    const step = set(this.getManifestEntry(stepKey)!, key, data);

    this.$commit(Mutation.SET__MANIFEST, {
      ...manifest,
      [stepKey]: merge(manifest[stepKey], {
        ...step,
        __system: {
          dirty: true,
          touched: manifest[stepKey].__system.touched || !automated,
        },
      }),
    });
  }

  // -------------------------------------------------------------------------------------------------------------------

  get manifestHasData() {
    return !isEqual(this.$state<Manifest>('manifest'), this.defaultState.manifest);
  }

  get manifestIsDirty() {
    return !!Object.values(this.$state<Manifest>('manifest')).filter((step) => step.__system.dirty).length;
  }

  get manifestIsTouched() {
    return !!Object.values(this.$state<Manifest>('manifest')).filter((step) => step.__system.touched).length;
  }

  /**
   * Check if an analysis (or his computing) is in progress
   */
  get analysisOrComputeInProgress() {
    const stepAnalysis = this.getManifestEntry(StepsKeys.Analysis);

    return (
      stepAnalysis?.flags.analysis.isPending ||
      stepAnalysis?.flags.analysis.waitStarted ||
      stepAnalysis?.flags.analysis.isStarted ||
      stepAnalysis?.flags.analysis.waitGettingData ||
      stepAnalysis?.flags.analysis.isGettingData ||
      stepAnalysis?.flags.analysis.waitCancelled ||
      stepAnalysis?.flags.compute.isComputing
    );
  }

  /**
   * Check if an analysis is completed (success or error)
   */
  get computeCompleted() {
    const stepAnalysis = this.getManifestEntry(StepsKeys.Analysis);

    return stepAnalysis?.flags.compute.isSuccess || stepAnalysis?.flags.compute.isFailure;
  }

  // -------------------------------------------------------------------------------------------------------------------

  /** Get stored step key  */
  set currentStepKey(stepKey: StepsKeys) {
    this.$commit(Mutation.SET__CURRENT_STEP_KEY, stepKey);
  }

  // -------------------------------------------------------------------------------------------------------------------

  /** Get stored patient id */
  get patientId() {
    return this.$state<State['patientId']>('patientId');
  }

  /** Get stored manifest */
  get manifest() {
    return this.$state<State['manifest']>('manifest');
  }

  /** Get stored steps */
  get steps() {
    return this.$state<State['steps']>('steps');
  }

  /** Get stored steps (with labels) */
  get stepsWithLabel(): StepsWithLabel[] {
    return this.$state<State['steps']>('steps').map((step: StepsKeys) => ({
      key: step,
      label: i18n.tc(`analysis.steps.${step}.label`),
    }));
  }

  /** Get stored step key  */
  get currentStepKey() {
    return this.$state<State['currentStepKey']>('currentStepKey');
  }

  /** Get stored entities */
  get entities() {
    return this.$state<State['entities']>('entities');
  }

  /** Get stored "patient" entity */
  get entityPatient() {
    return this.$state<State['entities']['patient']>('entities.patient');
  }

  /** Get stored "stations" entity */
  get entityStations() {
    return this.$state<State['entities']['stations']>('entities.stations');
  }

  /** Get stored "solesPairs" entity */
  get entitySolesPairs(): TFetch<TEntity<TSoles>> | null {
    return this.$state<State['entities']['solesPairs']>('entities.solesPairs');
  }

  /** Get stored "command" entity */
  get entityCommand() {
    return this.$state<State['entities']['command']>('entities.command');
  }
}
